import type { EmbedContent } from "@babackapp/private-api-client/generated/schema";

// `pnpm dev` runs plain `vite` on the embed app, which serves the transformed
// source and has no `/index.js` — that bundle only exists under `dist/`, i.e.
// after a build. Loading the source needs `type="module"` (see MODULE_SUFFIX).
const DEV_EMBED_URL = "http://localhost:5175/src/index.ts";
const MODULE_SUFFIX = ".ts";

// Shopify order ids are numeric. `orderId` comes straight from the page URL and
// ends up in the widget's iframe URL, so anything else is dropped.
const SHOPIFY_ORDER_ID = /^\d+$/;

type InjectEmbedScriptOptions = {
  noOverlayClose?: boolean;
  /**
   * Load the V3 embed whatever the shop's `admin.returnWidget.enabled` opt-in
   * says, used by the super-admin demo, whose whole point is previewing the
   * new portal on shops still running the legacy widget.
   */
  forceNewWidget?: boolean;
  /**
   * Shopify order id the V3 embed opens on (`data-order-id`), set by the
   * admin "Créer un retour Baback" action through `/o?orderId=`. The legacy
   * widget needs nothing here: it reads `orderId` from the page URL itself.
   * Non-numeric values are ignored.
   */
  orderId?: string;
  /**
   * Forces the V3 embed's language (`data-lang`), e.g. the shop's default.
   * Opened from the office there is no storefront locale to read, so without
   * this the portal falls back to the dashboard's own language (BBS-430).
   */
  lang?: string;
};

export function injectEmbedScript(
  embedData: EmbedContent,
  shopId: string,
  {
    noOverlayClose = true,
    forceNewWidget = false,
    orderId,
    lang,
  }: InjectEmbedScriptOptions = {},
) {
  if (document.querySelector("script[data-shop-id]")) return;

  // VITE_USE_LEGACY_WIDGET skips the local dev server (localhost:5175, always
  // the new widget) so the shop's V3 opt-in can be tested locally —
  // off for a shop means the API returns the legacy widget's script instead.
  const useNewWidgetDevServer =
    import.meta.env.DEV &&
    (forceNewWidget || import.meta.env.VITE_USE_LEGACY_WIDGET !== "true");
  const newWidgetUrl = forceNewWidget
    ? embedData.newEmbedScriptUrl
    : embedData.embedScriptUrl;
  const embedSrc = useNewWidgetDevServer ? DEV_EMBED_URL : newWidgetUrl;

  if (!embedSrc) {
    // Legacy widget: `window.baback.isOpen` is only read once, at the widget's
    // initial mount (see apps/return-widget/src/index.tsx checkIsOpen) — setting
    // it afterwards does nothing. This effect can re-run for the same shop
    // without the user doing anything (e.g. a tab-visibility change causing
    // React to reconnect passive effects), so re-running the bootstrap script
    // itself would re-append CDN <script> tags and reset an already-open
    // widget. `providerId` is set by the server-generated bootstrap script as
    // a raw literal and isn't guaranteed to be a string at runtime despite the
    // type, so compare loosely.
    const legacyBaback = (window as unknown as Record<string, unknown>)
      .baback as { providerId?: string } | undefined;
    if (
      legacyBaback?.providerId !== undefined &&
      String(legacyBaback.providerId) === String(shopId)
    ) {
      return;
    }

    const scriptFn = new Function(embedData.script);
    scriptFn();
    return;
  }

  const s = document.createElement("script");
  s.src = embedSrc;
  // Vite serves the dev entry as an ES module. `document.currentScript` is null
  // in a module, but the embed already falls back to querying [data-shop-id].
  if (embedSrc.endsWith(MODULE_SUFFIX)) s.type = "module";
  s.setAttribute("data-shop-id", shopId);
  s.setAttribute("data-base", window.location.origin);
  s.setAttribute("data-auto-open", "true");
  s.setAttribute("data-position", embedData.widgetPosition ?? "center");
  s.setAttribute("data-advanced", "true");
  if (noOverlayClose) s.setAttribute("data-no-overlay-close", "true");
  if (orderId && SHOPIFY_ORDER_ID.test(orderId)) {
    s.setAttribute("data-order-id", orderId);
  }
  if (lang) s.setAttribute("data-lang", lang);
  s.async = true;
  document.head.appendChild(s);
}
